import type { XpAccountBalance } from 'src/types/xp';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { XP_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

/**
 * GET /xp/balance — the XP ledger of the account's wallet. The route is wallet-scoped
 * (`@Protected({ forWallet: true })` on the api) and the guard resolves the account session to
 * the account's primary wallet, so it is asked only for an account that has one: a visitor and an
 * account with no wallet used to get two 401s on `/profile` for a balance they could not have
 * (app#155). Keyed by the session it is read under, like the deals (app#248).
 */
export function useXpBalance(options?: { enabled?: boolean }) {
  const session = useRequestSession();

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: QueryKeys.forSession(QueryKeys.xpBalance(), session),
    queryFn: async (): Promise<XpAccountBalance> => {
      const response = await axios.get<XpAccountBalance>('/xp/balance');
      return response.data;
    },
    staleTime: XP_STALE_TIME,
  });
}
